import ProxyImage from "@/components/ProxyImage";
import {
    PurchasingOrderBuyerRemarkModel,
    PurchasingLabelSkuModel,
    PurchasingLabelStatisticsExportItem,
    PurchasingOrderSellerRemarkModel,
    StatsModel,
} from "@/services/api/moreFeatures/purchasingLabel/purchasingLabelStatistics";
import React from "react";
import { getShowPurchasingLabelStatisticsExportItem } from "./util";
import { uniq } from "lodash";
import SellerRemarkFlagIcon from "@/components/SellerRemarkFlagIcon";
import { Empty } from "antd";
import { isEmpty } from "@/utils";
import { DeleteOutlined } from "@ant-design/icons";

interface PurchasingLabelStatisticsProductTableProps {
    statsModels: StatsModel[];
    exportItems: PurchasingLabelStatisticsExportItem[];
    className: string;
    onDeleteMarketWholesaler?: (statsModelIndex: number) => void;
    onDeleteSku?: (statsModelIndex: number, productIndex: number, skuIndex: number) => void;
}

interface OrderRemarkModel {
    skuModels: PurchasingLabelSkuModel[];
    sellerRemarkModels: PurchasingOrderSellerRemarkModel[];
    buyerRemarkModels: PurchasingOrderBuyerRemarkModel[];
}

const RemarkRows: React.FC<{
    productSkus: PurchasingLabelSkuModel[];
    shownItemLength: number;
    showSellerRemark: boolean;
    showBuyerRemark: boolean;
}> = ({ productSkus, shownItemLength, showSellerRemark, showBuyerRemark }) => {
    /** 存储一个订单包含的所有SKU Model信息 */
    const originalId2ModelMapping = new Map<string, OrderRemarkModel>();

    function getOrderRemarkModel(orderOriginalId: string): OrderRemarkModel {
        const orderRemarkModel = originalId2ModelMapping.get(orderOriginalId);

        if (orderRemarkModel) return orderRemarkModel;

        const newOrderRemarkModel: OrderRemarkModel = {
            skuModels: [],
            sellerRemarkModels: [],
            buyerRemarkModels: [],
        };

        originalId2ModelMapping.set(orderOriginalId, newOrderRemarkModel);

        return newOrderRemarkModel;
    }

    productSkus.forEach((sku) => {
        if (showSellerRemark) {
            sku.orderSellerRemarks?.forEach((orderSellerRemark) => {
                /** 找到当前SKU有哪些订单正在使用，存储到Map中 */
                const model = getOrderRemarkModel(orderSellerRemark.orderOriginalId);

                model.skuModels.push(sku);
                model.sellerRemarkModels.push(orderSellerRemark);
            });
        }

        if (showBuyerRemark) {
            sku.orderBuyerRemarks?.forEach((orderBuyerRemark) => {
                const model = getOrderRemarkModel(orderBuyerRemark.orderOriginalId);

                model.skuModels.push(sku);
                model.buyerRemarkModels.push(orderBuyerRemark);
            });
        }
    });

    const trs: React.ReactNode[] = [];

    originalId2ModelMapping.forEach((orderRemarkModel, orderOriginalId) => {
        /** 理论上SKU属性是不需要去重的，如果出现重复，证明数据出现了问题 */
        const skuProperties = uniq(orderRemarkModel.skuModels.map((sku) => sku.name));

        /** 同一个订单O假如包含 SKU1和SKU2两个商品，当订单O添加备注和旗帜时，SKU1和SKU2都会有和订单O一样的备注和旗帜 */
        /** 此时需要将备注和旗帜进行去重展示，效果如:订单A订单编号，sku1属性；sku2属性，旗帜：xx，备注：xxx */
        const flags = uniq(
            orderRemarkModel.sellerRemarkModels.map((sellerRemarkModel) => sellerRemarkModel.flag).filter(Boolean),
        );
        const sellerRemarks = uniq(
            orderRemarkModel.sellerRemarkModels
                .map((sellerRemarkModel) => sellerRemarkModel.content)
                .filter(Boolean),
        );
        const buyerRemarks = uniq(
            orderRemarkModel.buyerRemarkModels.map((buyerRemarkModel) => buyerRemarkModel.content).filter(Boolean),
        );
        const hasSellerRemark = flags.length > 0 || sellerRemarks.length > 0;
        const hasBuyerRemark = buyerRemarks.length > 0;

        trs.push(
            <tr key={orderOriginalId} style={{ backgroundColor: "#f2f6ff" }}>
                <td colSpan={shownItemLength}>
                    <div style={{ display: "flex" }}>
                        <span className="tl" style={{ width: 300 }}>
                            订单编号:&nbsp;{orderOriginalId}
                        </span>
                        <span className="tl" style={{ width: 300 }}>
                            属性:&nbsp;{skuProperties.join()}
                        </span>
                        <span
                            className="tl ml20"
                            style={{
                                flex: 1,
                                wordBreak: "break-all",
                                overflowWrap: "anywhere",
                            }}
                        >
                            {showBuyerRemark && hasBuyerRemark && (
                                <span style={{ display: "block" }}>买家留言:{buyerRemarks.join()}</span>
                            )}
                            {showSellerRemark && hasSellerRemark && (
                                <span style={{ display: "block" }}>
                                    卖家备注:
                                    <span>
                                        {flags.map((flag, index) => {
                                            return <SellerRemarkFlagIcon orderSellerRemarksFlag={flag} key={index} />;
                                        })}
                                    </span>
                                    <span>{sellerRemarks.join()}</span>
                                </span>
                            )}
                        </span>
                    </div>
                </td>
            </tr>,
        );
    });

    return <>{trs}</>;
};

interface SkuRowsProps {
    skus: PurchasingLabelSkuModel[];
    productPicture: string;
    title: string;
    productExternalId: string;
    showProductPicture: boolean;
    showProductTitle: boolean;
    showProductExternalId: boolean;
    showSkuPicture: boolean;
    showSkuProperty: boolean;
    showSkuExternalId: boolean;
    showSkuPrice: boolean;
    showSkuQuantity: boolean;
    showTotal: boolean;
    total: number;
    onDeleteSku?: (statsModelIndex: number, productIndex: number, skuIndex: number) => void;
    statsModelIndex: number;
    productIndex: number;
}

const SkuRows: React.FC<SkuRowsProps> = ({
    skus,
    productPicture,
    title,
    productExternalId,
    showProductPicture,
    showProductTitle,
    showProductExternalId,
    showSkuPicture,
    showSkuProperty,
    showSkuExternalId,
    showSkuPrice,
    showSkuQuantity,
    showTotal,
    total,
    onDeleteSku,
    statsModelIndex,
    productIndex,
}) => (
    <>
        {skus.map((sku, skuIndex) => (
            <tr key={skuIndex}>
                {skuIndex === 0 && showProductPicture && (
                    <td rowSpan={skus.length}>
                        <ProxyImage src={productPicture} width="50" />
                    </td>
                )}
                {skuIndex === 0 && showProductTitle && <td rowSpan={skus.length}>{title}</td>}
                {skuIndex === 0 && showProductExternalId && <td rowSpan={skus.length}>{productExternalId}</td>}
                {showSkuPicture && (
                    <td>
                        <ProxyImage src={sku.picture} width="50" />
                    </td>
                )}
                {showSkuProperty && <td>{sku.name}</td>}
                {showSkuExternalId && <td>{sku.externalId}</td>}
                {showSkuPrice && <td>{sku.price}</td>}
                {showSkuQuantity && (
                    <td>
                        <span>{sku.quantity}</span>

                        {onDeleteSku ? (
                            <DeleteOutlined
                                className="delete-btn ml3"
                                onClick={() => onDeleteSku(statsModelIndex, productIndex, skuIndex)}
                            />
                        ) : null}
                    </td>
                )}
                {skuIndex === 0 && showTotal && <td rowSpan={skus.length}>{total}</td>}
            </tr>
        ))}
    </>
);

interface MarketWholesalerRowProps {
    statsModel: StatsModel;
    shownItemLength: number;
    statsModelIndex: number;
    onDeleteMarketWholesaler: (statsModelIndex: number) => void;
}

const MarketWholesalerRow: React.FC<MarketWholesalerRowProps> = React.memo((props) => {
    const { statsModel, shownItemLength, statsModelIndex, onDeleteMarketWholesaler } = props;

    const buildMarketWholesalerContent = () => {
        if (statsModel.marketWholesaler === "") return "无";

        const showMarket = !isEmpty(statsModel.market) && statsModel.market !== "";
        const showWholesaler = !isEmpty(statsModel.market) && statsModel.market !== "";

        return (
            <>
                {showMarket ? <span className="fb">{statsModel.market}</span> : null}
                {showMarket && showWholesaler ? <span className="ml3">-</span> : null}
                {showWholesaler ? <span className="ml3">{statsModel.wholesaler}</span> : null}
            </>
        );
    };

    return (
        <tr>
            <td colSpan={shownItemLength - 1}>
                市场-档口：
                <span className="error ml5">{buildMarketWholesalerContent()}</span>
            </td>
            <td>
                共<span className="error">{statsModel.total}</span>件
                {onDeleteMarketWholesaler ? (
                    <DeleteOutlined className="delete-btn" onClick={() => onDeleteMarketWholesaler(statsModelIndex)} />
                ) : null}
            </td>
        </tr>
    );
});

const PurchasingLabelStatisticsProductTable: React.FC<PurchasingLabelStatisticsProductTableProps> = function (props) {
    const { statsModels, exportItems, className, onDeleteMarketWholesaler, onDeleteSku } = props;

    const {
        showProductPicture,
        showProductTitle,
        showProductExternalId,
        showSkuPicture,
        showSkuProperty,
        showSkuExternalId,
        showSkuPrice,
        showSkuQuantity,
        showTotal,
        showSellerRemark,
        showBuyerRemark,
        shownItemLength,
    } = getShowPurchasingLabelStatisticsExportItem(exportItems);

    function buildTbodyContent() {
        if (!statsModels?.length) {
            return (
                <tr>
                    <td colSpan={shownItemLength}>
                        <Empty description="统计数据为空" />
                    </td>
                </tr>
            );
        }

        return (
            <>
                {statsModels.map((statsModel, statsModelIndex) => {
                    const marketWholesaler = (statsModel.market ?? "") + (statsModel.wholesaler ?? "");

                    return (
                        <React.Fragment key={marketWholesaler}>
                            <MarketWholesalerRow
                                shownItemLength={shownItemLength}
                                statsModel={statsModel}
                                statsModelIndex={statsModelIndex}
                                onDeleteMarketWholesaler={onDeleteMarketWholesaler}
                            />

                            {statsModel.productModels.map((productModel, productIndex) => (
                                <React.Fragment key={productIndex}>
                                    <SkuRows
                                        skus={productModel.skus}
                                        productPicture={productModel.picture}
                                        title={productModel.title}
                                        productExternalId={productModel.externalId}
                                        showProductPicture={showProductPicture}
                                        showProductTitle={showProductTitle}
                                        showProductExternalId={showProductExternalId}
                                        showSkuPicture={showSkuPicture}
                                        showSkuProperty={showSkuProperty}
                                        showSkuExternalId={showSkuExternalId}
                                        showSkuPrice={showSkuPrice}
                                        showSkuQuantity={showSkuQuantity}
                                        showTotal={showTotal}
                                        total={productModel.total}
                                        onDeleteSku={onDeleteSku}
                                        statsModelIndex={statsModelIndex}
                                        productIndex={productIndex}
                                    />
                                    {showSellerRemark || showBuyerRemark ? (
                                        <RemarkRows
                                            productSkus={productModel.skus}
                                            shownItemLength={shownItemLength}
                                            showSellerRemark={showSellerRemark}
                                            showBuyerRemark={showBuyerRemark}
                                        />
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    );
                })}
            </>
        );
    }

    return (
        <table
            className={`ec-purchasingLabelStatistics-table ${className}`}
            // @ts-ignore
            border={className === "print" ? 1 : undefined}
        >
            <thead>
                <tr>
                    {showProductPicture && <th className="product-img">商品图片</th>}
                    {showProductTitle && <th className="product-title">商品标题</th>}
                    {showProductExternalId && <th className="product-externalId">宝贝商家编码</th>}
                    {showSkuPicture && <th className="sku-img">单品图片</th>}
                    {showSkuProperty && <th className="sku-property">单品属性</th>}
                    {showSkuExternalId && <th className="sku-externalId">单品商家编码</th>}
                    {showSkuPrice && <th className="sku-price">单品价格</th>}
                    {showSkuQuantity && <th className="sku-quantity">单品数量</th>}
                    {showTotal && <th className="total">数量总和</th>}
                </tr>
            </thead>
            <tbody>{buildTbodyContent()}</tbody>
        </table>
    );
};

export default React.memo(PurchasingLabelStatisticsProductTable);
